import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';
import { TrendingUp, DollarSign, ShoppingCart, Users, ArrowUpRight, Building2, FileText } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { GroupAnalytics } from './GroupAnalytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

export const AdminAnalytics = () => {
  const { isGroupSA } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isGroupSA) fetchData();
  }, [isGroupSA]);

  // Group SA sees the consolidated Group Analytics view
  if (isGroupSA) return <GroupAnalytics />;

  const fetchData = async () => {
    try {
      const [dashboardRes, salesRes] = await Promise.all([
        fetch(`${API}/dashboard/admin`, { credentials: 'include' }),
        fetch(`${API}/sales`, { credentials: 'include' }),
      ]);
      
      let dashboardData = null;
      let salesData = [];
      
      if (dashboardRes.ok) {
        dashboardData = await dashboardRes.json();
      }
      
      if (salesRes.ok) {
        salesData = await salesRes.json();
      }

      // Process sales data for charts
      const salesByMonth = {};
      const salesByOffer = {};
      const salesByStatus = {};

      salesData.forEach(sale => {
        // By month
        const month = sale.created_at?.substring(0, 7) || 'Unknown';
        salesByMonth[month] = (salesByMonth[month] || 0) + sale.amount;

        // By offer
        salesByOffer[sale.offer] = (salesByOffer[sale.offer] || 0) + 1;

        // By status
        salesByStatus[sale.status] = (salesByStatus[sale.status] || 0) + 1;
      });

      // Convert to chart format
      const monthlyData = Object.entries(salesByMonth)
        .map(([month, amount]) => ({ month, amount }))
        .sort((a, b) => a.month.localeCompare(b.month))
        .slice(-6);

      const offerData = Object.entries(salesByOffer)
        .map(([name, value]) => ({ name, value }));

      const statusLabels = {
        devis_signe: 'Devis signés',
        facture_envoyee: 'Factures envoyées',
        paiement_recu: 'Paiements reçus',
        securisation: 'Sécurisation',
        commission_eligible: 'Commissions éligibles',
        paye: 'Payés',
      };

      const statusData = Object.entries(salesByStatus)
        .map(([status, value]) => ({ name: statusLabels[status] || status, value }));

      setData({
        dashboard: dashboardData,
        monthlyRevenue: monthlyData,
        offerDistribution: offerData,
        statusDistribution: statusData,
        totalSales: salesData.length,
      });
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-slate-200 rounded" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-slate-200 rounded-xl" />
          ))}
        </div>
        <div className="h-80 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  const stats = [
    {
      title: 'CA Total',
      value: `${(data?.dashboard?.total_revenue || 0).toLocaleString('fr-FR')} €`,
      icon: DollarSign,
      color: 'bg-indigo-500',
    },
    {
      title: 'CA Mensuel',
      value: `${(data?.dashboard?.monthly_revenue || 0).toLocaleString('fr-FR')} €`,
      icon: TrendingUp,
      color: 'bg-emerald-500',
    },
    {
      title: 'Total Ventes',
      value: data?.totalSales || 0,
      icon: ShoppingCart,
      color: 'bg-violet-500',
    },
    {
      title: 'Commerciaux',
      value: data?.dashboard?.users_count || 0,
      icon: Users,
      color: 'bg-orange-500',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-[Manrope]" data-testid="admin-analytics-title">
          Analytics
        </h1>
        <p className="text-slate-500 mt-1">Vue d'ensemble des performances</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <Card 
            key={stat.title} 
            className="stat-card border-0 shadow-sm animate-fade-in"
            style={{ animationDelay: `${index * 0.05}s` }}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className={`p-3 rounded-xl ${stat.color}`}>
                  <stat.icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
              </div>
              <div className="mt-4">
                <p className="text-sm font-medium text-slate-500">{stat.title}</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                  {stat.value}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Revenue Chart */}
        <Card className="border-0 shadow-sm" data-testid="monthly-chart">
          <CardHeader>
            <CardTitle className="font-[Manrope]">CA par mois</CardTitle>
            <CardDescription>Évolution du chiffre d'affaires</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.monthlyRevenue || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip 
                    formatter={(value) => [`${value.toLocaleString('fr-FR')} €`, 'CA']}
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="amount" fill="#4f46e5" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Offer Distribution */}
        <Card className="border-0 shadow-sm" data-testid="offer-chart">
          <CardHeader>
            <CardTitle className="font-[Manrope]">Distribution par offre</CardTitle>
            <CardDescription>Répartition des ventes par type d'offre</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data?.offerDistribution || []}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {(data?.offerDistribution || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card className="border-0 shadow-sm lg:col-span-2" data-testid="status-chart">
          <CardHeader>
            <CardTitle className="font-[Manrope]">Statut des ventes</CardTitle>
            <CardDescription>Répartition par statut</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.statusDistribution || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" stroke="#64748b" fontSize={12} />
                  <YAxis dataKey="name" type="category" stroke="#64748b" fontSize={12} width={120} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="value" fill="#10b981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
